<template>
  <div class="insights-team-collaboration-widget insights-widget">
    <InsightsCollaborationIntegrationsDialog
      v-if="showIntegrationsDialog"
      :show="showIntegrationsDialog"
      :providers="widgetData.providers"
      :members-count="widgetData.members_count"
      :integrations-url="widgetData.integrations_url"
      @close="showIntegrationsDialog = false"
    />

    <InsightsCollaborationDataLagDialog
      v-if="showDataLagDialog"
      :show="showDataLagDialog"
      @close="showDataLagDialog = false"
    />

    <div
      class="widget-wrapper"
      :class="{ 'blurred-bg': isStillSyncing }"
    >
      <div class="widget-common widget-small widget-full-height">
        <div class="widget-title d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <span>{{ title }}</span>
            <i
              v-tooltip="tooltipOptions"
              class="hi hi-info-filled ml-5"
              :title="tooltip"
            />
          </div>
          <a
            v-if="canManageIntegrations && (isIntegrationSyncing || isDataView)"
            href="#"
            class="insights-widget-view-more manage-integrations"
            @click.prevent="showIntegrationsDialog = true"
          >
            <i class="hi hi-external" />
            <span>Manage integrations</span>
          </a>
        </div>
        <div
          class="widget-body"
          :class="bodyClasses"
        >
          <div
            v-if="isReady && isEmptyState"
            class="collaboration-empty-state"
          >
            <div class="empty-state-icons">
              <span
                v-for="logo in providerLogos"
                :key="logo.alt"
                class="icon-tile"
              >
                <img
                  :src="logo.src"
                  :alt="logo.alt"
                />
              </span>
              <span class="dots">
                <span />
                <span />
                <span />
              </span>
              <span
                v-for="icon in collaborationTypeIcons"
                :key="icon"
                class="icon-tile type-icon"
              >
                <i class="material-symbols-rounded notranslate">{{ icon }}</i>
              </span>
            </div>
            <div class="empty-state-title">See how your team communicates</div>
            <div class="empty-state-text">Track emails, calls, and messages in one place.</div>
            <span
              v-tooltip="tooltipOptions"
              class="connect-integration"
              :title="connectTooltip"
            >
              <AppButton
                type="primary"
                :disabled="!canManageIntegrations"
                @click="showIntegrationsDialog = true"
              >
                Connect an integration
              </AppButton>
            </span>
          </div>
          <div
            v-else-if="isIntegrationSyncing || (isReady && isDataView)"
            class="d-flex"
            :class="isIntegrationSyncing ? null : bodyClasses"
          >
            <div class="insights-team-collaboration-widget-content">
              <InsightsIntegrationSyncStatus
                v-if="isIntegrationSyncing"
                title="Synchronizing collaboration data"
                :is-still-syncing="isStillSyncing"
              />
              <template v-else-if="!isEmptyWidget">
                <div class="section">
                  <InsightsTeamCollaborationPill
                    type="email"
                    property="sent_count"
                    :value="widgetData.email.sent_count"
                    :providers="providersOf('email')"
                  />
                  <InsightsTeamCollaborationPill
                    type="email"
                    property="received_count"
                    :value="widgetData.email.received_count"
                    :providers="providersOf('email')"
                  />
                  <InsightsTeamCollaborationPill
                    type="email"
                    property="read_count"
                    :value="widgetData.email.read_count"
                    :providers="providersOf('email')"
                  />
                </div>
                <div class="section">
                  <InsightsTeamCollaborationPill
                    type="call"
                    property="total_count"
                    :value="widgetData.call.total_count"
                    :providers="providersOf('call')"
                  />
                  <InsightsTeamCollaborationPill
                    type="call"
                    property="duration"
                    :value="widgetData.call.duration"
                    :providers="providersOf('call')"
                  />
                  <InsightsTeamCollaborationPill
                    type="message"
                    property="total_count"
                    :value="messagesSentCount"
                    :providers="providersOf('message')"
                  />
                </div>
                <p
                  v-if="isDataLagging"
                  class="collaboration-data-lag"
                >
                  {{ dataLagText }}
                  <a
                    href="#"
                    @click.prevent="showDataLagDialog = true"
                    >Learn more.</a
                  >
                </p>
              </template>
              <InsightsNoData
                v-else
                :type="emptyReason"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { INSIGHTS, INSIGHTS_ASSETS } from '~/app_constants';
import { mapGetters } from 'vuex';
import { Insights, Integrations } from '~/mixins';
import AppButton from '~/components/AppButton.vue';
import InsightsTeamCollaborationPill from '~/components/insights/InsightsTeamCollaborationPill.vue';
import InsightsIntegrationSyncStatus from '~/components/insights/InsightsIntegrationSyncStatus.vue';
import InsightsCollaborationDataLagDialog from '~/components/insights/InsightsCollaborationDataLagDialog.vue';
import InsightsCollaborationIntegrationsDialog from '~/components/insights/InsightsCollaborationIntegrationsDialog.vue';

export default {
  name: 'InsightsTeamCollaborationWidget',
  components: {
    AppButton,
    InsightsTeamCollaborationPill,
    InsightsIntegrationSyncStatus,
    InsightsCollaborationDataLagDialog,
    InsightsCollaborationIntegrationsDialog,
    InsightsNoData: defineAsyncComponent(() => import('~/components/insights/InsightsNoData.vue')),
  },
  mixins: [Insights, Integrations],
  data: () => ({
    widgetKey: INSIGHTS.api.collaborations,
    tooltip: INSIGHTS.tooltips.collaborations.content,
    retryFetch: INSIGHTS.collaborations.retryFetch,
    tooltipOptions: Object.freeze({
      placement: 'top',
      html: true,
    }),
    title: 'Team Collaboration',
    collaborationTypeIcons: Object.freeze(['mail', 'call', 'chat']),
    showDataLagDialog: false,
    showIntegrationsDialog: false,
  }),
  computed: {
    ...mapGetters({
      widgetData: 'collaborations',
      currentUserRole: 'currentUserRole',
    }),
    canManageIntegrations() {
      return this.currentUserRole === 'owner' || this.currentUserRole === 'manager';
    },
    connectTooltip() {
      return this.canManageIntegrations ? '' : 'Only owners and managers can set up integrations';
    },
    isEmptyState() {
      return this.widgetData.state === 'empty';
    },
    isIntegrationSyncing() {
      return this.widgetData.state === 'syncing';
    },
    isDataView() {
      return this.widgetData.state === 'ready';
    },
    providerLogos() {
      return this.widgetData.providers.map(({ type }) => INSIGHTS_ASSETS.collaboration_provider_logos[type]);
    },
    isDataLagging() {
      return this.widgetData.lagging_providers.length > 0;
    },
    dataLagText() {
      const names = this.widgetData.lagging_providers.map((type) => INSIGHTS.collaborations.providers[type].name);

      return `Recent ${names.join(' and ')} data is still syncing.`;
    },
    messagesSentCount() {
      const { message } = this.widgetData;

      return message.public_message_count + message.private_message_count;
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    providersOf(collaborationType) {
      return this.widgetData.providers
        .filter((provider) => provider.status !== 'not_connected')
        .filter((provider) => provider.collaboration_types.includes(collaborationType))
        .map((provider) => provider.type);
    },
  },
};
</script>
